import { useAtomValue } from 'jotai';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  BulkEditView,
  EquipmentBrowserConfig,
  EquipmentBrowserViewResponse,
  EquipmentDetailsResponse,
} from '@repo/sdk/models/components';

import { useGaiaClient } from '@/hooks/use-gaia-client';
import { getEquipmentBrowserConfigQuery } from '@/queries/gaia-api/get-equipment-browser-config-query';
import { getEquipmentBrowserViewQuery } from '@/queries/gaia-api/get-equipment-browser-view-query';
import { getEquipmentBulkEditViewQuery } from '@/queries/gaia-api/get-equipment-bulk-edit-view-query';
import { getEquipmentDetailsQuery } from '@/queries/gaia-api/get-equipment-details-query';
import { previewEquipmentBrowserViewQuery } from '@/queries/gaia-api/preview-equipment-browser-view-query';
import { previewEquipmentBulkEditViewQuery } from '@/queries/gaia-api/preview-equipment-bulk-edit-view-query';
import { previewEquipmentDetailsQuery } from '@/queries/gaia-api/preview-equipment-details-query';
import { GaiaQueryWithAgentId } from '@/queries/types';
import { displayTemplatePreviewAtom } from '@/state/display-template-preview.state';

export interface UseGetEquipmentBrowserViewProps extends GaiaQueryWithAgentId {
  viewId?: string;
}

export const useGetEquipmentBrowserView = ({
  agentId,
  viewId,
}: UseGetEquipmentBrowserViewProps): UseQueryResult<EquipmentBrowserViewResponse | undefined> => {
  const client = useGaiaClient();
  const displayTemplate = useAtomValue(displayTemplatePreviewAtom);

  return useQuery(
    displayTemplate
      ? previewEquipmentBrowserViewQuery({ agentId, viewId, client, displayTemplate })
      : getEquipmentBrowserViewQuery({ agentId, viewId, client })
  );
};

export const useGetEquipmentBrowserConfig = ({
  agentId,
}: GaiaQueryWithAgentId): UseQueryResult<EquipmentBrowserConfig | undefined> => {
  const client = useGaiaClient();
  return useQuery(getEquipmentBrowserConfigQuery({ agentId, client }));
};

export interface UseGetEquipmentDetailsProps extends GaiaQueryWithAgentId {
  deviceId: string;
}

export const useGetEquipmentDetails = ({
  agentId,
  deviceId,
}: UseGetEquipmentDetailsProps): UseQueryResult<EquipmentDetailsResponse | undefined> => {
  const client = useGaiaClient();
  const displayTemplate = useAtomValue(displayTemplatePreviewAtom);

  return useQuery(
    displayTemplate
      ? previewEquipmentDetailsQuery({
          agentId,
          deviceId,
          client,
          displayTemplate,
        })
      : getEquipmentDetailsQuery({ agentId, deviceId, client })
  );
};

export interface UseGetEquipmentBulkEditViewProps extends GaiaQueryWithAgentId {
  deviceKind?: string;
  deviceIds?: string[];
}

export const useGetEquipmentBulkEditView = ({
  agentId,
  deviceKind,
  deviceIds,
}: UseGetEquipmentBulkEditViewProps): UseQueryResult<BulkEditView | undefined> => {
  const client = useGaiaClient();
  const displayTemplate = useAtomValue(displayTemplatePreviewAtom);

  return useQuery(
    displayTemplate
      ? previewEquipmentBulkEditViewQuery({
          agentId,
          deviceKind,
          deviceIds,
          client,
          displayTemplate,
        })
      : getEquipmentBulkEditViewQuery({ agentId, deviceKind, deviceIds, client })
  );
};
